import { createSlice, type Draft, type PayloadAction } from '@reduxjs/toolkit';
import { getLogger } from '../../libs/logger';
import { disableAnimationIfNeeded } from './thunks';
import { type ScrollTag } from '../../hooks/useScrollToTag';
import { type PersonAccordionTab } from '@doxyme/ui/components/patterns';

export const logger = getLogger('UISlice');

export type UIState = {
  isChatOpen: boolean;
  activeUserAccordion?: string | undefined;
  /**
   * One-time flag to skip auto-opening the first sidebar accordion.
   * Used by chat toast "View" action to preserve target user selection.
   */
  shouldSkipSidebarAutoOpenOnce?: boolean;
  /**
   * The active tab per user accordion (keyed by user ID).
   * Shared across dashboard waiting room and in-call panels.
   */
  activeUserAccordionTabs: Record<string, PersonAccordionTab>;
  isPictureInPictureStarted: boolean;
  isCallMenuOpen?: boolean;
  /**
   * Check if accordion has already been expanded during the call.
   */
  isSelfPreviewExpanded?: boolean;
  /**
   * Indicates whether all CSS animation is disabled
   */
  isAnimationDisabled?: boolean;
  scrollTag: ScrollTag | null;
  /**
   * The bottom offset for floating call controls
   */
  callControlsFloatingBottom: number;
  /**
   * Whether the user has seen the apps popover
   */
  hasSeenAppsPopover: boolean;
  chatFocusRequest?: { userId: string };
};

/* istanbul ignore next */
const getInitialState = (): UIState => ({
  isChatOpen: false,
  activeUserAccordion: undefined,
  shouldSkipSidebarAutoOpenOnce: false,
  activeUserAccordionTabs: {},
  isPictureInPictureStarted: false,
  isCallMenuOpen: false,
  isSelfPreviewExpanded: false,
  isAnimationDisabled: false,
  scrollTag: null,
  callControlsFloatingBottom: 0,
  hasSeenAppsPopover: true,
  chatFocusRequest: undefined,
});

/* istanbul ignore next */
export const _setIsChatOpen = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.isChatOpen = action.payload;
};

/* istanbul ignore next */
export const _setActiveUserAccordionId = (
  state: Draft<UIState>,
  action: PayloadAction<string | undefined>,
): void => {
  state.activeUserAccordion = action.payload;
};

export const _setShouldSkipSidebarAutoOpenOnce = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.shouldSkipSidebarAutoOpenOnce = action.payload;
};

/* istanbul ignore next */
export const _setActiveUserAccordionTab = (
  state: Draft<UIState>,
  action: PayloadAction<{
    userId: string;
    tab: PersonAccordionTab | undefined;
  }>,
): void => {
  const { userId, tab } = action.payload;
  if (tab === undefined) {
    delete state.activeUserAccordionTabs[userId];
  } else {
    state.activeUserAccordionTabs[userId] = tab;
  }
};

/* istanbul ignore next */
export const _setIsPictureInPictureStarted = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.isPictureInPictureStarted = action.payload;
};

/* istanbul ignore next */
export const _setIsCallMenuOpen = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.isCallMenuOpen = action.payload;
};

/* istanbul ignore next */
export const _setIsSelfPreviewExpanded = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.isSelfPreviewExpanded = action.payload;
};

/* istanbul ignore next */
export const _setScrollTag = (
  state: Draft<UIState>,
  action: PayloadAction<ScrollTag | null>,
): void => {
  state.scrollTag = action.payload;
};

/* istanbul ignore next */
export const _setHasSeenAppsPopover = (
  state: Draft<UIState>,
  action: PayloadAction<boolean>,
): void => {
  state.hasSeenAppsPopover = action.payload;
};

export const _requestChatFocus = (
  state: Draft<UIState>,
  action: PayloadAction<string>,
) => {
  state.chatFocusRequest = { userId: action.payload };
};

/* istanbul ignore next */
export const _setOpenChatView = (
  state: Draft<UIState>,
  action: PayloadAction<{ userId: string; tab: PersonAccordionTab }>,
): void => {
  const { userId, tab } = action.payload;

  state.isChatOpen = true;
  state.activeUserAccordion = userId;
  state.shouldSkipSidebarAutoOpenOnce = true;
  state.activeUserAccordionTabs[userId] = tab;
};

/* istanbul ignore next */
export const uiStateSlice = createSlice({
  name: 'uiState',
  initialState: () => getInitialState(),
  reducers: {
    setIsChatOpen: _setIsChatOpen,
    setActiveUserAccordionId: _setActiveUserAccordionId,
    setShouldSkipSidebarAutoOpenOnce: _setShouldSkipSidebarAutoOpenOnce,
    setActiveUserAccordionTab: _setActiveUserAccordionTab,
    setIsPictureInPictureStarted: _setIsPictureInPictureStarted,
    setIsCallMenuOpen: _setIsCallMenuOpen,
    setIsSelfPreviewExpanded: _setIsSelfPreviewExpanded,
    setScrollTag: _setScrollTag,
    setOpenChatView: _setOpenChatView,
    setHasSeenAppsPopover: _setHasSeenAppsPopover,
    requestChatFocus: _requestChatFocus,
  },
  extraReducers: (builder) => {
    builder.addCase(disableAnimationIfNeeded.fulfilled, (state, action) => {
      state.isAnimationDisabled = action.payload;
    });
  },
});

export const {
  setIsChatOpen,
  setActiveUserAccordionId,
  setShouldSkipSidebarAutoOpenOnce,
  setActiveUserAccordionTab,
  setIsPictureInPictureStarted,
  setIsCallMenuOpen,
  setIsSelfPreviewExpanded,
  setScrollTag,
  setOpenChatView,
  setHasSeenAppsPopover,
  requestChatFocus,
} = uiStateSlice.actions;
